export type PhoneCountry = {
  iso: string;
  name: string;
  flag: string;
  callingCode: string;
  example: string;
  groups: number[];
};

export const phoneCountries: readonly PhoneCountry[] = [
  { iso: "UZ", name: "Узбекистан", flag: "🇺🇿", callingCode: "+998", example: "90 123 45 67", groups: [2, 3, 2, 2] },
  { iso: "RU", name: "Россия", flag: "🇷🇺", callingCode: "+7", example: "912 345 67 89", groups: [3, 3, 2, 2] },
  { iso: "KZ", name: "Казахстан", flag: "🇰🇿", callingCode: "+7", example: "701 234 56 78", groups: [3, 3, 2, 2] },
  { iso: "TJ", name: "Таджикистан", flag: "🇹🇯", callingCode: "+992", example: "90 123 45 67", groups: [2, 3, 2, 2] },
  { iso: "KG", name: "Кыргызстан", flag: "🇰🇬", callingCode: "+996", example: "555 123 456", groups: [3, 3, 3] },
  { iso: "TM", name: "Туркменистан", flag: "🇹🇲", callingCode: "+993", example: "61 123456", groups: [2, 6] },
  { iso: "AZ", name: "Азербайджан", flag: "🇦🇿", callingCode: "+994", example: "50 123 45 67", groups: [2, 3, 2, 2] },
  { iso: "GE", name: "Грузия", flag: "🇬🇪", callingCode: "+995", example: "555 12 34 56", groups: [3, 2, 2, 2] },
  { iso: "AM", name: "Армения", flag: "🇦🇲", callingCode: "+374", example: "91 123456", groups: [2, 6] },
  { iso: "TR", name: "Турция", flag: "🇹🇷", callingCode: "+90", example: "532 123 45 67", groups: [3, 3, 2, 2] },
  { iso: "AE", name: "ОАЭ", flag: "🇦🇪", callingCode: "+971", example: "50 123 4567", groups: [2, 3, 4] },
  { iso: "SA", name: "Саудовская Аравия", flag: "🇸🇦", callingCode: "+966", example: "50 123 4567", groups: [2, 3, 4] },
  { iso: "IN", name: "Индия", flag: "🇮🇳", callingCode: "+91", example: "98765 43210", groups: [5, 5] },
  { iso: "PK", name: "Пакистан", flag: "🇵🇰", callingCode: "+92", example: "300 1234567", groups: [3, 7] },
  { iso: "CN", name: "Китай", flag: "🇨🇳", callingCode: "+86", example: "138 0013 8000", groups: [3, 4, 4] },
  { iso: "KR", name: "Южная Корея", flag: "🇰🇷", callingCode: "+82", example: "10 1234 5678", groups: [2, 4, 4] },
  { iso: "JP", name: "Япония", flag: "🇯🇵", callingCode: "+81", example: "90 1234 5678", groups: [2, 4, 4] },
  { iso: "US", name: "США / Канада", flag: "🇺🇸", callingCode: "+1", example: "202 555 0147", groups: [3, 3, 4] },
  { iso: "GB", name: "Великобритания", flag: "🇬🇧", callingCode: "+44", example: "7700 900123", groups: [4, 6] },
  { iso: "DE", name: "Германия", flag: "🇩🇪", callingCode: "+49", example: "151 23456789", groups: [3, 8] },
  { iso: "FR", name: "Франция", flag: "🇫🇷", callingCode: "+33", example: "6 12 34 56 78", groups: [1, 2, 2, 2, 2] },
] as const;

export function getPhoneCountry(iso?: string, callingCode?: string): PhoneCountry {
  return phoneCountries.find((item) => item.iso === iso)
    ?? phoneCountries.find((item) => item.callingCode === callingCode)
    ?? phoneCountries[0]!;
}

export function splitPhoneNumber(value?: string, preferredIso?: string, preferredCallingCode?: string) {
  const preferred = getPhoneCountry(preferredIso, preferredCallingCode);
  const raw = (value ?? "").trim();
  const international = raw.startsWith("+") || raw.startsWith("00");
  const digits = raw.replace(/\D/g, "");
  if (!international || !digits) return { country: preferred, nationalNumber: cleanNationalDigits(digits, preferred) };

  const normalizedDigits = raw.startsWith("00") ? digits.slice(2) : digits;
  const matches = [...phoneCountries]
    .sort((left, right) => right.callingCode.length - left.callingCode.length)
    .filter((item) => normalizedDigits.startsWith(item.callingCode.slice(1)));
  let country = matches.find((item) => item.iso === preferred.iso) ?? matches[0] ?? preferred;
  const sharedSevenNational = normalizedDigits.startsWith("7") ? normalizedDigits.slice(1) : "";
  if (/^[67]/.test(sharedSevenNational)) {
    country = getPhoneCountry("KZ");
  }
  const nationalNumber = cleanNationalDigits(normalizedDigits.slice(country.callingCode.length - 1), country);
  return { country, nationalNumber };
}

export function normalizePhoneNumber(nationalNumber: string, iso?: string, callingCode?: string): string {
  const country = getPhoneCountry(iso, callingCode);
  const parsed = splitPhoneNumber(nationalNumber, country.iso, country.callingCode);
  const digits = cleanNationalDigits(parsed.nationalNumber, parsed.country);
  return digits ? `${parsed.country.callingCode}${digits}` : "";
}

export function formatNationalPhone(value: string, iso?: string): string {
  const country = getPhoneCountry(iso);
  const digits = value.replace(/\D/g, "");
  const parts: string[] = [];
  let offset = 0;
  for (const size of country.groups) {
    if (offset >= digits.length) break;
    parts.push(digits.slice(offset, offset + size));
    offset += size;
  }
  if (offset < digits.length) parts.push(digits.slice(offset));
  return parts.join(" ");
}

function cleanNationalDigits(value: string, country: PhoneCountry): string {
  const digits = value.replace(/\D/g, "");
  const expectedLength = country.groups.reduce((sum, size) => sum + size, 0);
  if (country.callingCode === "+7" && digits.length === 11 && digits.startsWith("8")) return digits.slice(1);
  if (digits.length === expectedLength + 1 && digits.startsWith("0")) return digits.slice(1);
  return digits;
}
